import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { UserProject } from '../entities/UserProject';
import { Subject } from 'rxjs';
import { InFilter } from '../baseServices/filter.interface';
import { StoreName } from '../entities/StoreName';
import { DataChangeEmitPayload, DataChangeEmitWho, DataChangeEmitWhat } from '../models/data-change-emit/data-change-emit-payload';
import { DbTransaction } from '../models/db/db-transaction';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { EntityHelper, Check } from '../utils/entity-helper';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';
import { GlobalConstants } from '../constant/constant';

@Injectable({
  providedIn: 'root',
})
export class UserProjectService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<UserProject>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(userProject: UserProject, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: userProject, who: who, what: what });
  }

  getEmitChangeCallback(userProject: UserProject, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForUserProject + '_' + userProject.RecordId,
      fn: this.emitChange.bind(this, userProject, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.Users2Project], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getUserProjectByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<UserProject> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<UserProject>(StoreName.Users2Project, onlineId, UserProject, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as UserProject;

    if (this.checkIsUserProjectSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getUserProjectByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<UserProject> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<UserProject>(StoreName.Users2Project, recordId, UserProject, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as UserProject;

    if (this.checkIsUserProjectSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getUserProjectsByUserId(userId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<UserProject[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<UserProject>(StoreName.Users2Project, GlobalConstants.KEY_NAMES.UserId, userId, UserProject, transaction);

    let records = result as UserProject[];

    if (!includeDeleted) {
      records = records.filter(userProject => !this.checkIsUserProjectSoftDeleted(userProject));
    }

    return records;
  }

  // #endregion
  // region Low-level Create

  private async createUserProject(who: DataChangeEmitWho, recordToCreate: UserProject, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateUserProjectCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.Users2Project, transaction);

    // LC5. Get created record
    const createdRecord = await this.getUserProjectByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateUserProject(who: DataChangeEmitWho, recordToUpdate: UserProject, skipLocalCopyValidation: boolean, localCopy: UserProject | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getUserProjectByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateUserProjectUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<UserProject>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<UserProject>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateUserProject(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.Users2Project, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getUserProjectByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateUserProject(userProject: UserProject, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateUserProjectCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateUserProjectUpdate(userProject: UserProject, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get Many

  async getProjectIdsByUserId(userId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number[]> {
    const records = await this.getUserProjectsByUserId(userId, existingTransaction, includeDeleted);
    return records.map(record => record.ProjectId);
  }

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertUserProjectFromSync(who: DataChangeEmitWho, recordToUpsert: UserProject, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getUserProjectByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateUserProject(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createUserProject(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsUserProjectSoftDeleted(record: UserProject): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: UserProject, b: UserProject): boolean {
    return a.UserId === b.UserId && a.ProjectId === b.ProjectId;
  }

  // #endregion
  // region Lookup

  // #endregion
  // region Transform

  // #endregion
  // region Other

  // #endregion
}
